import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Upload as UploadIcon, Loader2, CheckCircle2, AlertCircle,
  Sparkles, FileSpreadsheet, Building, Package, School as SchoolIcon,
  LayoutGrid, Coins, ListChecks, RotateCw, ShieldAlert, HelpCircle, Settings2,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import ImportDiffPreview from '../components/ImportDiffPreview';
import ManualMappingModal from '../components/ManualMappingModal';
import ManualReviewModal from '../components/ManualReviewModal';
import { dynamicSupplyPreview, dynamicSupplyCommit, dynamicSupplyCommitManual, dynamicSupplyRepick } from '../api';

/**
 * Dynamic Supply Workbook Importer
 * ------------------------------------------------------------------
 * Upload ANY supplier distribution Excel (pivot layout: items across
 * columns, schools down rows). The backend auto-detects worksheet,
 * header row, supplier row(s), unit price row, and execution columns.
 *
 * Flow:
 *   1. Drop file → POST /api/supply/dynamic-preview → analysis
 *   2. Show detected supplier(s), sheet, schools count, item breakdown,
 *      5-school sample, per-sheet stats
 *   3. If supplier is already in DB → 3-option prompt (merge / replace / cancel)
 *   4. Commit → POST /api/supply/dynamic-commit
 */
export default function SupplyDynamicImportPage() {
  const nav = useNavigate();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [supplierOverride, setSupplierOverride] = useState('');
  const [mode, setMode] = useState('merge');
  const [committed, setCommitted] = useState(null);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [showDiff, setShowDiff] = useState(false);            // ← NEW: diff-preview step
  const [showManualMapping, setShowManualMapping] = useState(false);
  const [manualMapping, setManualMapping] = useState(null);   // ← if user completes manual mapping, this holds {mapping, supplier}

  const reset = () => {
    setAnalysis(null); setError(null); setSupplierOverride('');
    setMode('merge'); setCommitted(null); setShowDuplicatePrompt(false);
    setShowDiff(false); setShowManualMapping(false); setManualMapping(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setBusy(true); setError(null); setAnalysis(null); setCommitted(null); setManualMapping(null);
    try {
      const res = await dynamicSupplyPreview(file);
      setAnalysis(res);
      setSupplierOverride(res.supplier_detected || '');
      if (res.existing_supplier) setShowDuplicatePrompt(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to analyze workbook');
    } finally { setBusy(false); }
  }, []);

  const [blockedReconciliation, setBlockedReconciliation] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [manualReviewApplied, setManualReviewApplied] = useState(false);

  /** Runs the FINAL commit — used by ImportDiffPreview's confirm button. */
  const finalizeCommit = useCallback(async (chosenMode) => {
    if (!analysis?.token) return;
    setCommitting(true); setError(null);
    try {
      const finalMode = chosenMode || mode;
      const res = manualMapping
        ? await dynamicSupplyCommitManual({
            token: analysis.token,
            supplier: manualMapping.supplier,
            mapping: manualMapping.mapping,
            mode: finalMode,
          })
        : await dynamicSupplyCommit({
            token: analysis.token,
            supplier: supplierOverride || analysis.supplier_detected,
            mode: finalMode,
          });
      if (res && res.blocked) {
        // Server declined the commit — the authoritative raw-vs-normalized
        // check failed.  NO bypass is offered; the analyst must open the
        // manual review to see per-item mismatches.
        setBlockedReconciliation(res.raw_vs_normalized || res.reconciliation);
        return;
      }
      setBlockedReconciliation(null);
      setCommitted(res);
      setShowDuplicatePrompt(false);
      setShowDiff(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Import failed');
    } finally { setCommitting(false); }
  }, [analysis, supplierOverride, mode, manualMapping]);

  /** Starts the diff-preview step. Called by duplicate-prompt or the main commit button. */
  const openDiff = useCallback((chosenMode) => {
    if (chosenMode) setMode(chosenMode);
    setShowDuplicatePrompt(false);
    setShowDiff(true);
  }, []);

  const onManualMappingProceed = useCallback((result) => {
    setManualMapping(result);
    setShowManualMapping(false);
    setShowDiff(true);
  }, []);

  /** Re-classify a sheet as school_data / kpi_summary / ignore and
   *  re-analyze the workbook.  The override is per-file (per token). */
  const onSheetTypeChange = useCallback(async (sheetName, newType) => {
    if (!analysis?.token) return;
    const prev = analysis;
    const nextOverrides = {
      ...(prev.sheet_overrides || {}),
      [sheetName]: newType,
    };
    setBusy(true); setError(null);
    try {
      const res = await dynamicSupplyRepick({ token: analysis.token, sheet_types: nextOverrides });
      setAnalysis(res);
      if (res.supplier_detected && !supplierOverride) {
        setSupplierOverride(res.supplier_detected);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to re-classify sheet');
    } finally { setBusy(false); }
  }, [analysis, supplierOverride]);

  const needsSupplier = analysis && (analysis.needs_supplier_confirmation || !supplierOverride);
  const itemsFound = analysis?.totals?.items || 0;
  const canCommit = analysis && itemsFound > 0 && (supplierOverride || analysis.supplier_detected);

  return (
    <div dir="rtl" className="supply-theme min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="supply-dynamic-import">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-slate-500 text-[11px] font-medium flex items-center gap-2">
              <button onClick={() => nav('/')} className="hover:text-slate-800">لوحة التوريد</button>
              <span className="text-slate-400">›</span>
              <span className="text-slate-700">مستورد التوريد الديناميكي</span>
            </div>
            <h1 className="text-[26px] font-extrabold tracking-tight mt-1 flex items-center gap-2" style={{ color: 'var(--se-ink)' }}>
              <Sparkles size={22} strokeWidth={1.75} style={{ color: 'var(--se-emerald)' }} />
              مستورد ملفات التوريد الذكي
            </h1>
            <div className="text-slate-600 text-[12.5px] mt-1 max-w-2xl">
              يقرأ أي ملف Excel لتوزيع الأصناف على المدارس مهما اختلف تنسيقه.
              يتعرف تلقائياً على المورد، الأصناف، أسعار الوحدة، والمدارس دون الحاجة إلى ربط أعمدة يدوياً.
            </div>
          </div>
          <div className="flex items-center gap-2">
            {analysis && (
              <>
                <button onClick={() => setShowManualMapping(true)} className="se-btn se-btn-ghost" data-testid="manual-mapping-btn">
                  <Settings2 size={13} /> ربط يدوي
                </button>
                <button onClick={reset} className="se-btn se-btn-ghost" data-testid="reset-btn">
                  <RotateCw size={13} /> استيراد ملف جديد
                </button>
              </>
            )}
            <button onClick={() => nav('/')} className="se-btn se-btn-ghost">
              <ArrowRight size={13} /> العودة
            </button>
          </div>
        </div>

        {/* Dropzone */}
        {!analysis && !busy && (
          <div className="se-panel p-10 text-center" data-testid="dropzone" style={{ border: '2px dashed var(--se-border-2)' }}>
            <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
              <FileSpreadsheet size={26} style={{ color: '#0F766E' }} />
            </div>
            <div className="text-[18px] font-bold text-[color:var(--se-ink)] mb-2">اسحب ملف Excel هنا أو اختر ملفاً</div>
            <div className="text-slate-600 text-[12.5px] mb-5 max-w-lg mx-auto">
              يدعم <code style={{ color: '#0F766E' }}>.xlsx / .xlsm / .xlsb / .xls / .ods</code>.
              مثال: ملفات توزيع الأثاث المكتبي، الأنشطة البدنية، مختبرات العلوم — كل مورد بتنسيقه.
            </div>
            <label className="se-btn se-btn-primary inline-flex cursor-pointer" data-testid="upload-btn">
              <UploadIcon size={14} /> اختيار ملف Excel
              <input ref={inputRef} type="file" hidden accept=".xlsx,.xlsm,.xlsb,.xls,.ods"
                onChange={(e) => onFile(e.target.files?.[0])} data-testid="file-input" />
            </label>
          </div>
        )}

        {/* Loading */}
        {busy && (
          <div className="se-panel p-10 text-center" data-testid="analyzing">
            <Loader2 size={30} className="animate-spin mx-auto mb-3" style={{ color: 'var(--se-emerald)' }} />
            <div className="text-[15px] font-semibold text-[color:var(--se-ink)]">جاري تحليل الملف...</div>
            <div className="text-slate-500 text-[12px] mt-1">يتم اكتشاف الموردين والأصناف والأسعار تلقائياً</div>
          </div>
        )}

        {/* Error */}
        {error && !busy && (
          <div className="se-panel p-5 mb-4 flex items-start gap-3" style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }} data-testid="error-panel">
            <AlertCircle size={18} className="text-red-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-red-900 text-[13.5px] font-semibold">تعذر تحليل الملف</div>
              <div className="text-red-800 text-[12.5px] mt-1 leading-relaxed">{error}</div>
            </div>
          </div>
        )}

        {/* Duplicate supplier prompt */}
        {showDuplicatePrompt && analysis?.existing_supplier && (
          <div className="se-panel p-5 mb-4" style={{ border: '1px solid #FCD34D', background: '#FFFBEB' }} data-testid="duplicate-prompt">
            <div className="flex items-start gap-3">
              <ShieldAlert size={18} style={{ color: '#D97706' }} className="shrink-0 mt-1" />
              <div className="flex-1">
                <div className="text-amber-900 text-[14px] font-bold">
                  المورد "{analysis.existing_supplier.name}" موجود مسبقاً في القاعدة
                </div>
                <div className="text-amber-800 text-[12.5px] mt-1">
                  عدد السجلات الحالية: <b>{analysis.existing_supplier.records.toLocaleString('en-US')}</b>.
                  اختر كيفية التعامل مع البيانات الجديدة:
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  <button data-testid="dup-merge" onClick={() => openDiff('merge')}
                    className="text-right p-3 rounded-lg transition"
                    style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
                    <div className="text-emerald-800 text-[13px] font-bold">تحديث ودمج البيانات</div>
                    <div className="text-emerald-700 text-[11.5px] mt-1">إضافة الجديد وتحديث السجلات المشتركة (الأمن — لا يحذف)</div>
                  </button>
                  <button data-testid="dup-replace" onClick={() => openDiff('replace_supplier')}
                    className="text-right p-3 rounded-lg transition"
                    style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
                    <div className="text-red-800 text-[13px] font-bold">استبدال بيانات هذا المورد</div>
                    <div className="text-red-700 text-[11.5px] mt-1">حذف كل سجلات المورد الحالية واستبدالها بالملف الجديد</div>
                  </button>
                  <button data-testid="dup-cancel" onClick={() => setShowDuplicatePrompt(false)}
                    className="text-right p-3 rounded-lg transition"
                    style={{ background: '#FFFFFF', border: '1px solid var(--se-border)' }}>
                    <div className="text-slate-800 text-[13px] font-bold">إلغاء</div>
                    <div className="text-slate-500 text-[11.5px] mt-1">لا يتم إجراء أي تعديل، معاينة فقط</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Committed success */}
        {committed && (
          <div className="se-panel p-5 mb-4 flex items-start gap-3" style={{ background: '#ECFDF5', border: '1px solid #A7F3D0' }} data-testid="commit-success">
            <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-emerald-900 text-[14px] font-bold">تم استيراد البيانات بنجاح</div>
              <div className="text-emerald-800 text-[12.5px] mt-1">
                <span>الإجمالي: <b>{committed.total?.toLocaleString('en-US')}</b> سجل</span>
                <span className="mx-2">·</span>
                <span>جديد: <b>{committed.inserted?.toLocaleString('en-US')}</b></span>
                <span className="mx-2">·</span>
                <span>محدَّث: <b>{committed.updated?.toLocaleString('en-US')}</b></span>
                <span className="mx-2">·</span>
                <span>الوضع: <b>{committed.mode === 'merge' ? 'دمج' : 'استبدال المورد'}</b></span>
              </div>
              <div className="text-emerald-700 text-[12px] mt-2">
                الموردون: {(committed.suppliers_imported || []).join('، ') || '—'}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => nav('/')} className="se-btn se-btn-primary">فتح لوحة التوريد</button>
                <button onClick={reset} className="se-btn se-btn-ghost">استيراد ملف آخر</button>
              </div>
            </div>
          </div>
        )}

        {/* Diff preview (safe confirm step) */}
        {showDiff && analysis?.token && (
          <div className="mb-4">
            <div className="mb-3 flex items-center gap-2">
              <button data-testid="open-manual-review"
                      onClick={() => setShowReview(true)}
                      className="se-btn se-btn-ghost flex items-center gap-2">
                <Settings2 size={14} /> مراجعة وتعديل البيانات
              </button>
              {manualReviewApplied && (
                <span className="px-2 py-1 rounded text-[11px] font-semibold"
                      style={{ background: 'rgba(139,92,246,0.15)', color: '#C4B5FD', border: '1px solid rgba(139,92,246,0.35)' }}>
                  ✓ تمت مراجعة يدوية
                </span>
              )}
            </div>
            <ImportDiffPreview
              token={analysis.token}
              supplier={manualMapping?.supplier || supplierOverride || analysis.supplier_detected}
              mode={mode}
              mapping={manualMapping?.mapping}
              committing={committing}
              onConfirm={() => finalizeCommit(mode)}
              onCancel={() => { setShowDiff(false); setBlockedReconciliation(null); }}
            />
            {blockedReconciliation && (
              <div className="p-4 rounded-lg mt-3" data-testid="blocked-mismatch-banner"
                   style={{ background: '#FEF2F2', border: '1px solid #FCA5A5' }}>
                <div className="text-[13px] font-bold text-red-900 mb-2">تم إيقاف الاستيراد — خام Excel لا يطابق السجلات المطبّعة</div>
                <div className="text-red-800 text-[12px] mb-3 tabular-nums">
                  خام: {Number(blockedReconciliation?.totals?.raw_value || blockedReconciliation?.calculated?.value || 0).toLocaleString('en-US')} ر.س ·
                  مطبّع: {Number(blockedReconciliation?.totals?.normalized_value || blockedReconciliation?.source?.value || 0).toLocaleString('en-US')} ر.س ·
                  فرق: {Number(blockedReconciliation?.totals?.value_difference || blockedReconciliation?.difference?.value || 0).toLocaleString('en-US')} ر.س
                </div>
                <div className="flex gap-2">
                  <button data-testid="open-manual-review-mismatch" onClick={() => setShowReview(true)}
                          className="se-btn se-btn-ghost flex items-center gap-2">
                    <Settings2 size={14} /> فتح المراجعة اليدوية
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {showReview && analysis?.token && (
          <ManualReviewModal
            token={analysis.token}
            supplier={manualMapping?.supplier || supplierOverride || analysis.supplier_detected || 'المورد'}
            onClose={() => setShowReview(false)}
            onCommitReady={() => { setManualReviewApplied(true); }}
          />
        )}

        {/* Manual mapping modal */}
        {showManualMapping && analysis?.token && (
          <ManualMappingModal
            token={analysis.token}
            onCancel={() => setShowManualMapping(false)}
            onProceed={onManualMappingProceed}
          />
        )}

        {/* Analysis preview */}
        {analysis && !committed && !showDiff && (
          <div className="space-y-4">
            <PreviewSummary analysis={analysis} onSheetTypeChange={onSheetTypeChange} />
            <PreCommitValidationBanner analysis={analysis}
                                       supplier={supplierOverride || analysis.supplier_detected} />
            <SupplierBar
              analysis={analysis}
              supplierOverride={supplierOverride}
              setSupplierOverride={setSupplierOverride}
            />
            {itemsFound === 0 && <ManualMappingHint analysis={analysis} onManual={() => setShowManualMapping(true)} />}
            <ItemsTable items={analysis.items} />
            <SampleSchoolsTable sample={analysis.sample_schools} />

            {/* Commit button */}
            {!showDuplicatePrompt && (
              <div className="se-panel p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="text-slate-600 text-[12.5px]">
                  {canCommit
                    ? `جاهز للاستيراد: ${analysis.totals?.schools?.toLocaleString('en-US')} مدرسة × ${itemsFound} صنف`
                    : needsSupplier
                      ? 'يرجى تحديد اسم المورد أعلاه أولاً'
                      : 'لا يمكن الاستيراد تلقائياً — استخدم الربط اليدوي'}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => setShowManualMapping(true)} className="se-btn se-btn-ghost" data-testid="manual-mapping-btn-strip">
                    <Settings2 size={13} /> ربط يدوي
                  </button>
                  <button onClick={() => openDiff(mode)} disabled={!canCommit || committing}
                    data-testid="commit-btn"
                    className="se-btn se-btn-primary disabled:opacity-40">
                    {committing ? <Loader2 size={13} className="animate-spin" /> : <UploadIcon size={13} />}
                    معاينة التغييرات والاستيراد
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function PreviewSummary({ analysis, onSheetTypeChange }) {
  const t = analysis.totals || {};
  const cards = [
    { icon: SchoolIcon, label: 'عدد المدارس', value: t.schools, color: '#22D3EE' },
    { icon: Package,   label: 'عدد الأصناف', value: t.items,   color: '#3B82F6' },
    { icon: LayoutGrid,label: 'إجمالي الكميات', value: t.total_quantity, color: '#10B981' },
    { icon: Coins,     label: 'القيمة التقديرية', value: t.total_value, color: '#F59E0B', money: true },
  ];
  const TYPE_LABEL = {
    school_data: 'بيانات مدارس',
    kpi_summary: 'ملخص / KPI',
    ignore: 'تجاهل',
  };
  return (
    <div className="se-panel p-4" data-testid="preview-summary">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[15px] font-bold text-[color:var(--se-ink)] flex items-center gap-2">
            <FileSpreadsheet size={16} style={{ color: 'var(--se-emerald)' }} />
            {analysis.filename}
          </div>
          <div className="text-slate-500 text-[11.5px] mt-1">
            الورقة المختارة: <span className="text-slate-800">{analysis.picked_sheet}</span>
            <span className="mx-2">·</span>
            صف العنوان: <span className="text-slate-800">{analysis.header_row + 1}</span>
            <span className="mx-2">·</span>
            الأوراق: {(analysis.all_sheets || []).length}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="se-panel p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: `${c.color}18`, border: `1px solid ${c.color}45` }}>
              <c.icon size={16} style={{ color: c.color }} />
            </div>
            <div className="min-w-0">
              <div className="text-slate-500 text-[10.5px]">{c.label}</div>
              <div className="text-[18px] font-extrabold text-[color:var(--se-ink)] tabular-nums leading-tight">
                {c.value == null ? '—' : c.money
                  ? Number(c.value).toLocaleString('en-US', { maximumFractionDigits: 0 })
                  : Number(c.value).toLocaleString('en-US')}
                {c.money && <span className="text-slate-500 text-[11px] mr-1">ريال</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Per-sheet mini table — with per-file classification dropdown so
          the analyst can promote a summary sheet to school_data or ignore
          a noisy sheet without touching the global importer. */}
      {(analysis.sheets_meta || []).length > 0 && (
        <div className="mt-4 se-panel overflow-x-auto" style={{ padding: 0 }} data-testid="sheets-table">
          <table className="se-table">
            <thead>
              <tr>
                <th>الورقة</th>
                <th>التصنيف</th>
                <th>عدد المدارس</th>
                <th>عدد الأصناف</th>
                <th>النتيجة</th>
              </tr>
            </thead>
            <tbody>
              {analysis.sheets_meta.map((s, i) => {
                const type = s.type || s.auto_type || (s.usable ? 'school_data' : 'ignore');
                return (
                  <tr key={i} style={s.sheet === analysis.picked_sheet ? { background: '#F0FDFA' } : {}}>
                    <td>
                      {s.sheet}
                      {s.sheet === analysis.picked_sheet && <span className="se-pill se-pill-teal mr-2">مختارة</span>}
                    </td>
                    <td>
                      <select
                        data-testid={`sheet-type-${i}`}
                        value={type}
                        onChange={(e) => onSheetTypeChange?.(s.sheet, e.target.value)}
                        className="se-input"
                        style={{ padding: '4px 8px', minWidth: 140,
                                 background: '#FFFFFF', color: '#1E293B',
                                 border: '1px solid var(--se-border)' }}
                      >
                        <option value="school_data">{TYPE_LABEL.school_data}</option>
                        <option value="kpi_summary">{TYPE_LABEL.kpi_summary}</option>
                        <option value="ignore">{TYPE_LABEL.ignore}</option>
                      </select>
                      {s.auto_type && s.auto_type !== type && (
                        <div className="text-[10px] text-slate-400 mt-1">تلقائي: {TYPE_LABEL[s.auto_type] || s.auto_type}</div>
                      )}
                    </td>
                    <td className="tabular-nums">{s.school_count?.toLocaleString('en-US') || '—'}</td>
                    <td className="tabular-nums">{s.item_count?.toLocaleString('en-US') || '—'}</td>
                    <td className="text-slate-500 tabular-nums">{s.score ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Pre-commit validation strip that always shows the analyst the exact
 *  sheet, header row, mapping summary, school count, total qty and total
 *  value before the commit action.  Colour-coded by reconciliation status.
 */
function PreCommitValidationBanner({ analysis, supplier }) {
  const t = analysis.totals || {};
  // AUTHORITATIVE reconciliation: raw row-level Excel sums vs the exact
  // records that will be saved.  Excel SUBTOTAL / summary rows are never
  // consulted here — they may appear separately as informational only.
  const rvn = analysis.raw_vs_normalized || {};
  const rvnTotals = rvn.totals || {};
  const ok = !!rvn.ok;
  const cfg = ok
    ? { bg: '#ECFDF5', bd: '#A7F3D0', ink: '#065F46', label: 'مطابقة الخام مع السجلات المطبّعة — PASS' }
    : { bg: '#FEF2F2', bd: '#FCA5A5', ink: '#991B1B', label: 'خام Excel لا يطابق السجلات المطبّعة — BLOCKED' };
  const infoFields = analysis.school_info_fields || [];
  const excelSrc = analysis.reconciliation?.source || {}; // informational
  return (
    <div className="se-panel p-4" data-testid="precommit-banner"
         style={{ background: cfg.bg, border: `1px solid ${cfg.bd}` }}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert size={14} style={{ color: cfg.ink }} />
        <div className="text-[13px] font-bold" style={{ color: cfg.ink }}>{cfg.label} — التحقق قبل الحفظ</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <ValStat label="الورقة" value={analysis.picked_sheet || '—'} />
        <ValStat label="صف العنوان" value={(analysis.header_row ?? -1) >= 0 ? (analysis.header_row + 1) : '—'} />
        <ValStat label="المورد" value={supplier || 'غير محدد'} />
        <ValStat label="عدد الأصناف" value={Number(t.items || 0).toLocaleString('en-US')} />
        <ValStat label="كمية خام (Excel)" value={Number(rvnTotals.raw_quantity || 0).toLocaleString('en-US')} />
        <ValStat label="كمية مطبّعة (تُحفظ)" value={Number(rvnTotals.normalized_quantity || 0).toLocaleString('en-US')} />
        <ValStat label="قيمة خام" value={Number(rvnTotals.raw_value || 0).toLocaleString('en-US') + ' ر.س'} />
        <ValStat label="قيمة مطبّعة" value={Number(rvnTotals.normalized_value || 0).toLocaleString('en-US') + ' ر.س'} />
      </div>
      <div className="text-[11.5px] mt-2 tabular-nums" style={{ color: cfg.ink }}>
        فرق الكمية: <b>{Number(rvnTotals.quantity_difference || 0).toLocaleString('en-US')}</b> ·
        فرق القيمة: <b>{Number(rvnTotals.value_difference || 0).toLocaleString('en-US')} ر.س</b>
      </div>
      {infoFields.length > 0 && (
        <div className="text-[11px] mt-3" style={{ color: cfg.ink }}>
          <b>الحقول المكتشفة:</b> {infoFields.join(' · ')}
        </div>
      )}
      {(excelSrc.quantity != null || excelSrc.value != null) && (
        <div className="text-[10.5px] mt-3 text-slate-500">
          <b>ملاحظة:</b> إجماليات SUBTOTAL من الملف (معلوماتية فقط، لا تُستخدم للتحقق):
          &nbsp; كمية: {excelSrc.quantity != null ? Number(excelSrc.quantity).toLocaleString('en-US') : '—'}
          &nbsp; · قيمة: {excelSrc.value != null ? Number(excelSrc.value).toLocaleString('en-US') + ' ر.س' : '—'}
        </div>
      )}
    </div>
  );
}

function ValStat({ label, value }) {
  return (
    <div>
      <div className="text-slate-500 text-[10.5px]">{label}</div>
      <div className="text-[13px] font-bold text-[color:var(--se-ink)] tabular-nums truncate" title={String(value)}>{value}</div>
    </div>
  );
}

function SupplierBar({ analysis, supplierOverride, setSupplierOverride }) {
  const detected = analysis.supplier_detected;
  const all = analysis.all_suppliers_detected || [];
  return (
    <div className="se-panel p-4" data-testid="supplier-bar">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building size={16} className="text-slate-600" />
          <div className="text-[13.5px] font-bold text-[color:var(--se-ink)]">
            {detected ? 'المورد المكتشف' : 'المورد لم يُكتشف — يرجى الإدخال يدوياً'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[280px]">
          <input
            value={supplierOverride}
            onChange={(e) => setSupplierOverride(e.target.value)}
            placeholder="اسم المورد (يمكن التعديل)"
            data-testid="supplier-input"
            className="se-input flex-1"
          />
          {detected && supplierOverride !== detected && (
            <button onClick={() => setSupplierOverride(detected)} className="se-btn se-btn-ghost">استرجاع المكتشف</button>
          )}
        </div>
      </div>
      {all.length > 1 && (
        <div className="text-slate-500 text-[11.5px] mt-3">
          موردون آخرون تم اكتشافهم في الملف:
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {all.map((s) => (
              <button key={s} onClick={() => setSupplierOverride(s)}
                className="px-2 py-1 rounded-md text-[11px] transition"
                style={{
                  border: '1px solid ' + (supplierOverride === s ? '#99F6E4' : 'var(--se-border)'),
                  background: supplierOverride === s ? '#F0FDFA' : '#FFFFFF',
                  color: supplierOverride === s ? '#0F766E' : '#334155',
                }}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemsTable({ items }) {
  if (!items || items.length === 0) return null;
  const total = items.reduce((sum, it) => sum + (Number(it.unit_price) || 0), 0);
  return (
    <div className="se-panel p-4" data-testid="items-table">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-[color:var(--se-ink)] flex items-center gap-2">
          <ListChecks size={15} style={{ color: 'var(--se-emerald)' }} /> الأصناف المكتشفة
        </div>
        <div className="text-slate-500 text-[11.5px]">
          {items.length} صنف · متوسط السعر {items.length > 0 ? Math.round(total / items.length).toLocaleString('en-US') : '—'} ريال
        </div>
      </div>
      <div className="se-panel overflow-x-auto" style={{ padding: 0 }}>
        <table className="se-table">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>اسم الصنف</th>
              <th>المورد</th>
              <th className="w-32">سعر الوحدة</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td className="text-slate-500 tabular-nums">{i + 1}</td>
                <td>{it.name}</td>
                <td className="text-slate-500">{it.supplier || '—'}</td>
                <td className="tabular-nums">
                  {it.unit_price != null ? Number(it.unit_price).toLocaleString('en-US') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SampleSchoolsTable({ sample }) {
  if (!sample || sample.length === 0) return null;
  return (
    <div className="se-panel p-4" data-testid="sample-schools">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-bold text-[color:var(--se-ink)] flex items-center gap-2">
          <SchoolIcon size={15} style={{ color: 'var(--se-emerald)' }} /> عيّنة من 5 مدارس
        </div>
        <div className="text-slate-500 text-[11.5px]">للتحقق قبل الاستيراد</div>
      </div>
      <div className="se-panel overflow-x-auto" style={{ padding: 0 }}>
        <table className="se-table">
          <thead>
            <tr>
              <th>الرقم الوزاري</th>
              <th>اسم المدرسة</th>
              <th>المنطقة</th>
              <th>المرحلة</th>
              <th>عدد الأصناف</th>
            </tr>
          </thead>
          <tbody>
            {sample.map((s, i) => (
              <tr key={i}>
                <td className="tabular-nums">{s.ministerial_id || '—'}</td>
                <td>{s.school_name || '—'}</td>
                <td className="text-slate-500">{s.region || '—'}</td>
                <td className="text-slate-500">{s.stage || '—'}</td>
                <td className="tabular-nums">
                  {Object.keys(s.items || {}).length}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ManualMappingHint({ analysis, onManual }) {
  return (
    <div className="se-panel p-5" style={{ border: '1px solid #FCD34D', background: '#FFFBEB' }} data-testid="manual-hint">
      <div className="flex items-start gap-3">
        <HelpCircle size={20} className="shrink-0 mt-0.5" style={{ color: '#D97706' }} />
        <div className="flex-1">
          <div className="text-amber-900 text-[14px] font-bold">تعذر استخراج الأصناف تلقائياً</div>
          <div className="text-amber-800 text-[12.5px] mt-2 leading-relaxed">
            تم التعرف على المدارس ({analysis.totals?.schools} مدرسة) لكن هيكل الأصناف غير واضح.
            استخدم <b>الربط اليدوي</b> لتحديد صف العناوين وأعمدة الأصناف بنفسك — أو تأكد أن الملف يحتوي على
            صف "سعر الوحدة" أعلى صف عناوين الأصناف.
          </div>
          <button onClick={onManual} className="se-btn se-btn-primary mt-3" data-testid="open-manual-mapping">
            <Settings2 size={13} /> فتح الربط اليدوي
          </button>
        </div>
      </div>
    </div>
  );
}
